/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import { getEntitiesForPolicyRule, getProductHierarchyForPolicyRule } from "../../../services/policy";
import { hideLoader, showLoader } from "../../loader/loader";
import interpolateString from '../../../utility/stringInterpolation';
import { GET_ENTITIES_DATA_ERROR, GET_HIERARCHY_DATA_ERROR } from "../../../shared/PolicyDialog/constants";
import { showErrorAction } from "../../error/errorActionCreator";
import { setPolicyEntitiesFilteredItems, setPolicyExpandedItems, setPolicyHierarchyItems, setEditPolicyData } from "./dataActionCreator";
import { ACTIVE_ENTITIES } from "./constants";

/**
 * Get all the entities for product hierarchy
 * @param dispatch - Disptach is used to dispatch the action to set the value
 */
export async function constructEntities(dispatch: Function, rowData: any) {
  let errorMessage = { message: '' };
  let mappedEntities: any = [];
  dispatch(showLoader)
  try {
    const response = await getEntitiesForPolicyRule();
    //Filter the entities based on the active flag
    //Map the response in key and displayname format
    mappedEntities = response.entities
      .filter(entity => entity.hierarchyDefinition.meta.status === ACTIVE_ENTITIES)
      .map(entity => {
        return {
          key: entity.hierarchyDefinition.hierarchyId.hierarchyId,
          displayName: entity.hierarchyDefinition.hierarchyId.hierarchyName,
          hierarchyLevelId: entity.hierarchyDefinition.hierarchyLevel.find(
            h =>
              h.hierarchyLevelNumber ===
              Math.max(...entity.hierarchyDefinition.hierarchyLevel.map(o => o.hierarchyLevelNumber), 0)
          )?.hierarchyLevelId
        };
      });
    mappedEntities = [{ key: 'select', displayName: 'Select', hierarchyLevelId: "N/A" }, ...mappedEntities];

  } catch (error: any) {
    errorMessage = {
      message: interpolateString([error], GET_ENTITIES_DATA_ERROR)
    };
  }
  finally {
    rowData && dispatch(setEditPolicyData(setEntityData(rowData, mappedEntities)));
    dispatch(setPolicyEntitiesFilteredItems(mappedEntities));
    dispatch(showErrorAction(errorMessage))
    dispatch(hideLoader);
  }
};
const setEntityData = (rowData: any, mappedEntities: any) => {
  const obj = JSON.parse(rowData.expression);
  const { hierarchyId } = obj.resource.keys[1];
  const entityData = mappedEntities.find((entity: any) => entity?.key === hierarchyId);
  return { entity: entityData };
};
/**
 * Get product hierarchy response
 * @param dispatch - Disptach is used to dispatch the action to set the value
 * @param event - Event provides event object
 * @param item - Selected item
 * @param policyEntityKey - Policy entity key
 * @param hierarchy - Actual hierarchy object
 * @param expandedItems - Provides all the expanded items
 */
export async function createHierarchy(dispatch: Function, event: any, item: any, policyEntityKey: string, hierarchy: any, expandedItems: string[]) {
  dispatch(showLoader);
  let requestPayload;
  const key = event ? event.key : policyEntityKey;
  if (item) {
    requestPayload = {
      filter: {
        and: [
          { eq: { 'itemHierarchyInformation.hierarchyId': key } },
          { eq: { 'itemHierarchyInformation.ancestry.memberId': item.key } }
        ]
      },
      sort: [{ 'itemHierarchyInformation.memberName': 'asc' }],
      pagination: { limit: 10000, offset: 0 }
    };
  } else {
    requestPayload = {
      filter: {
        and: [
          { eq: { 'itemHierarchyInformation.hierarchyId': key } },
          { eq: { 'itemHierarchyInformation.hierarchyLevelId': event.hierarchyLevelId } }
        ]
      },
      pagination: { limit: 10000, offset: 0 }
    };
  }
  try {
    const response = await getProductHierarchyForPolicyRule(requestPayload);
    let productItemHierarchyData = response.entities
      .map((entity: any) => entity.itemHierarchyLevelMember)
      .filter(
        (hierarchy: any) => hierarchy.itemHierarchyInformation.hierarchyId.toLowerCase() === key.toLowerCase()
      );
    const productItemHierarchyDataCount = productItemHierarchyData.length;
    dispatch(setPolicyHierarchyItems([generateHierarchy(productItemHierarchyData, hierarchy)]));
    if (item) {
      handleExpandItems(dispatch, productItemHierarchyDataCount, expandedItems, item);
    }
    dispatch(hideLoader);
  } catch (error: any) {
    if (error.response && error.response.status !== 404) {
      const errorMessage = {
        message: interpolateString([error.message], GET_HIERARCHY_DATA_ERROR)
      };
      dispatch(showErrorAction(errorMessage));

    };
    dispatch(hideLoader);
  }
};

/**
 * Function to handle the expand items 
 * @param dispatch - Disptach is used to dispatch the action to set the value
 * @param productItemHierarchyData - List of hierarchy
 * @param expandedItems - Expanded items list
 * @param item - Currrent expanded item
 */
const handleExpandItems = (dispatch: Function, productItemHierarchyData: any, expandedItems: string[], item: any) => {
  if (productItemHierarchyData > 0) {
    if (!expandedItems.includes(item['key'])) {
      const expanded = [...expandedItems];
      expanded.push(item['key']);
      dispatch(setPolicyExpandedItems(expanded));
    } else {
      const index = expandedItems.indexOf(item['key']);
      const expanded = [...expandedItems];
      expanded.splice(index, 1);
      dispatch(setPolicyExpandedItems(expanded));
    }
  }
};

/**
 * Generates hierarchical strucutre of item
 * @param productItemHierarchyData - List of hierarchy data
 * @param hierarchies - List of hierarchy
 * @returns - Final list of hierarchy
 */
const generateHierarchy = (productItemHierarchyData: any, hierarchies: any) => {
  //Parent Hierarchy
  let hierarchy = hierarchies.length > 0 ? hierarchies.shift() : {};

  //Loop for child hierarchy
  while (productItemHierarchyData.length > 0) {
    const item = productItemHierarchyData.shift();
    if (!item.itemHierarchyInformation.ancestry) {
      //Set the hierarchy custom properties
      hierarchy = {
        name: item.itemHierarchyLevelMemberId,
        level: item.itemHierarchyInformation.hierarchyLevelId,
        key: item.itemHierarchyLevelMemberId,
        value: `${item.itemHierarchyInformation.memberName} | ${item.itemHierarchyLevelMemberId}`,
        items: [{
          name: item.itemHierarchyLevelMemberId,
          value: `${item.itemHierarchyInformation.memberName} | ${item.itemHierarchyLevelMemberId}`
        }],
        children: []
      };
    } else {
      const parentMemberId = item.itemHierarchyInformation.ancestry[0].memberId;
      if (hierarchy && hierarchy.hasOwnProperty('key')) {
        const hierarchyList = [hierarchy];
        while (hierarchyList.length > 0) {
          const hierarchyItem: any = hierarchyList.shift();
          if (hierarchyItem) {
            if (parentMemberId.toLowerCase() === hierarchyItem.key.toLowerCase() && !hierarchyItem.children.some((c: any) => c.name === item.itemHierarchyLevelMemberId)) {
              hierarchy.items.push({
                name: item.itemHierarchyLevelMemberId,
                value: `${item.itemHierarchyInformation.memberName} | ${item.itemHierarchyLevelMemberId}`
              });
              hierarchyItem.children.push({
                name: item.itemHierarchyLevelMemberId,
                level: item.itemHierarchyInformation.hierarchyLevelId,
                key: item.itemHierarchyLevelMemberId,
                value: `${item.itemHierarchyInformation.memberName} | ${item.itemHierarchyLevelMemberId}`,
                children: []
              });
              productItemHierarchyData = productItemHierarchyData.filter(
                (p: any) => p.itemHierarchyLevelMemberId !== item.itemHierarchyLevelMemberId
              );
            } else {
              for (let i = 0; i < hierarchyItem.children.length; i++) {
                hierarchyList.push(hierarchyItem.children[i]);
              }
            }
          }
        }
      }
    }
  }
  hierarchy.items = hierarchy && hierarchy.items && hierarchy.items.sort((a: { value: string; }, b: { value: string; }) => {
    var valueA = a.value.toUpperCase();
    var valueB = b.value.toUpperCase();
    if (valueA > valueB) {
      return 1;
    }
    if (valueA < valueB) {
      return -1;
    }
    return 0;
  });
  return hierarchy;
};
